import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { listSugarBabyProfiles } from '@/services/sugar-profile.service';
import { SugarBabyProfile } from '@/types';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { LockKeyhole } from 'lucide-react'; // Icon for locked content
import { useLanguageUtils } from '@/context/language-context';

const TEASER_PROFILE_COUNT = 6;

// Basic Profile Card (similar to the one in SugarRelationshipsPage but with blur)
const BlurredProfileCard: React.FC<{ profile: SugarBabyProfile }> = ({ profile }) => {
  // Utiliser une image placeholder. Idéalement, l'URL viendrait du profil.
  const imageUrl = profile.profile_picture_url || `https://via.placeholder.com/300x200/cccccc/969696?text=Locked+Profile`; 

  return (
    <div className="relative border p-4 rounded-lg shadow overflow-hidden group">
      {/* Blurred content */}
      <div className="blur-sm filter group-hover:blur-none transition-all duration-300">
        {/* Remplacé Skeleton par img */}
        <img src={imageUrl} alt={`Profile of ${profile.pseudo}`} className="w-full h-40 object-cover rounded-md mb-2 bg-muted"/> 
        <h3 className="font-semibold">{profile.pseudo}</h3>
        <p className="text-sm text-muted-foreground">{profile.location?.city || 'Unknown City'}</p>
      </div>
      {/* Overlay icon when not hovered */}
      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:opacity-0 opacity-100 transition-opacity duration-300 pointer-events-none">
          <LockKeyhole className="w-8 h-8 text-white/90" />
      </div>
    </div>
  );
};

const SugarTeaserPage: React.FC = () => {
  const { t } = useTranslation(['sugar', 'common', 'navbar']);
  const { getUrlWithLanguage } = useLanguageUtils();
  const [profiles, setProfiles] = useState<SugarBabyProfile[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchTeaserProfiles = async () => {
      setIsLoading(true);
      // Fetch a small number of profiles for the teaser
      const result = await listSugarBabyProfiles({}, { page: 1, pageSize: TEASER_PROFILE_COUNT });
      if (result.success && result.data) {
        setProfiles(result.data.profiles);
      } else {
        console.error("Error fetching teaser profiles:", result.error);
        setProfiles([]); // Set empty array on error to avoid infinite loading feel
      }
      setIsLoading(false);
    };
    fetchTeaserProfiles();
  }, []);

  const renderTeaserGrid = () => {
    if (isLoading) {
      return (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[...Array(TEASER_PROFILE_COUNT)].map((_, i) => <Skeleton key={i} className="h-48 rounded-lg" />)}
        </div>
      );
    }
    if (!profiles || profiles.length === 0) {
      return <p className="text-center text-muted-foreground">{t('sugar:teaser_no_profiles')}</p>; // Message specific to teaser
    }
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {profiles.map(profile => (
          <BlurredProfileCard key={profile.id} profile={profile} />
        ))}
      </div>
    );
  };

  return (
    <Layout>
      <div className="container mx-auto py-12">
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <h1 className="text-4xl font-bold tracking-tight mb-4">{t('sugar:teaser_title')}</h1>
          <p className="text-lg text-muted-foreground mb-8">{t('sugar:teaser_subtitle')}</p>
        </div>

        {/* Blurred Profile Grid */}
        <div className="relative mb-12">
            {renderTeaserGrid()}
            {/* Gradient overlay to enhance the locked feeling */}
             {/* <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-background to-transparent pointer-events-none"></div> */}
        </div>

        {/* Call to Action Section */}
        <div className="bg-gradient-to-r from-primary/80 to-secondary/80 text-primary-foreground p-8 rounded-lg text-center shadow-lg">
          <h2 className="text-3xl font-semibold mb-4">{t('sugar:teaser_cta_title')}</h2>
          <p className="mb-6 max-w-xl mx-auto">{t('sugar:teaser_cta_text')}</p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
              <Button size="lg" asChild className="bg-white text-primary hover:bg-gray-100 shadow-md scale-105">
                 <Link to={getUrlWithLanguage('/pricing')}>{t('navbar.pricing')}</Link>
              </Button>
               <span className="text-sm font-medium">{t('common.or')}</span>
               <Button size="lg" asChild className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow">
                 <Link to={getUrlWithLanguage('/register')}>{t('navbar.register')}</Link> 
               </Button>
          </div>
        </div>

        {/* Optional: Add sections explaining benefits, how it works, testimonials etc. */}
        
      </div>
    </Layout>
  );
};

export default SugarTeaserPage; 